import { API_CONFIG, buildApiUrl } from '../config';

const apiCall = async (url, options = {}) => {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
  return data;
};

const ep = (key, params = {}) => buildApiUrl(API_CONFIG.endpoints.payroll[key], params);

export const payrollApi = {
  // --- Profiles ---
  getProfiles: async () => {
    const res = await apiCall(ep('profiles'));
    return res.data ?? res;
  },

  getProfile: async (coachId) => {
    const res = await apiCall(ep('profileByCoach', { coachId }));
    return res.data ?? res;
  },

  autoCreateProfile: async (coachId) => {
    const res = await apiCall(ep('autoCreateProfile', { coachId }), { method: 'POST' });
    return res.data ?? res;
  },

  updateProfile: async (coachId, updates) => {
    const res = await apiCall(ep('updateProfile', { coachId }), {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    return res.data ?? res;
  },

  deleteProfile: async (coachId) => {
    const res = await apiCall(ep('deleteProfile', { coachId }), { method: 'DELETE' });
    return res.data ?? res;
  },

  getCoachHistory: async (coachId) => {
    const res = await apiCall(ep('coachHistory', { coachId }));
    return res.data ?? res;
  },

  // --- Pay Periods ---
  getPayPeriods: async () => {
    const res = await apiCall(ep('payPeriods'));
    return res.data ?? res;
  },

  createPayPeriod: async (data) => {
    const res = await apiCall(ep('createPayPeriod'), {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return res.data ?? res;
  },

  updatePayPeriod: async (id, data) => {
    const res = await apiCall(ep('updatePayPeriod', { id }), {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return res.data ?? res;
  },

  deletePayPeriod: async (id) => {
    const res = await apiCall(ep('deletePayPeriod', { id }), { method: 'DELETE' });
    return res.data ?? res;
  },

  getCurrentPayPeriod: async () => {
    const res = await apiCall(ep('currentPayPeriod'));
    return res.data ?? res;
  },

  generatePayPeriods: async (count = 8) => {
    const res = await apiCall(ep('generatePayPeriods'), {
      method: 'POST',
      body: JSON.stringify({ count })
    });
    return res.data ?? res;
  },

  closePayPeriod: async (id) => {
    const res = await apiCall(ep('closePayPeriod', { id }), { method: 'POST' });
    return res.data ?? res;
  },

  markPayPeriodPaid: async (id, paymentData = {}) => {
    const res = await apiCall(ep('markPayPeriodPaid', { id }), {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
    return res.data ?? res;
  },

  // --- Payroll Entries ---
  getEntries: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.payPeriodId) params.append('payPeriodId', filters.payPeriodId);
    if (filters.coachId) params.append('coachId', filters.coachId);
    if (filters.status) params.append('status', filters.status);
    const qs = params.toString();
    const url = `${ep('entries')}${qs ? '?' + qs : ''}`;
    const res = await apiCall(url);
    return res.data ?? res;
  },

  markEntryPaid: async (entryId, paymentData = {}) => {
    const res = await apiCall(ep('markEntryPaid', { entryId }), {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
    return res.data ?? res;
  },

  // --- Dashboard ---
  getDashboard: async () => {
    const res = await apiCall(ep('dashboard'));
    return res.data ?? res;
  },

  // --- Reports ---
  getReport: async (type, params = {}) => {
    const query = new URLSearchParams({ type, ...params });
    const url = `${ep('reports')}?${query.toString()}`;
    const res = await apiCall(url);
    return res.data ?? res;
  },

  downloadReportCsv: async (type, params = {}) => {
    const query = new URLSearchParams({ type, format: 'csv', ...params });
    const url = `${ep('reports')}?${query.toString()}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }
};

export default payrollApi;
